import { Link } from 'react-router-dom';
import { Shield, Lock, Cloud, Chrome, CreditCard, Key, Zap, Eye, EyeOff, ArrowRight, CheckCircle, Sparkles, Bookmark, StickyNote } from 'lucide-react';
import { useEffect, useState } from 'react';
import { 
  getFreePlan, 
  getRecurringPlan, 
  formatPrice,
  getDisplayPaymentMethods,
  type PaymentMethod 
} from '@safermark/shared';
import { getEnvironmentConfig } from '@safermark/config';
import ContactForm from '../components/ContactForm';
import { apiClient } from '@safermark/shared/api';

// Floating particles component
const FloatingParticles = () => {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {[...Array(20)].map((_, i) => (
        <div
          key={i}
          className="absolute w-1 h-1 bg-blue-400/30 rounded-full animate-pulse"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 3}s`,
            animationDuration: `${2 + Math.random() * 3}s`,
          }}
        />
      ))}
    </div>
  );
};

// Animated encryption text component
const EncryptedText = ({ originalText, className = "" }: { originalText: string, className?: string }) => {
  const [isEncrypted, setIsEncrypted] = useState(false);
  const [displayText, setDisplayText] = useState(originalText);
  
  const scrambleChars = "!@#$%^&*()_+-=[]{}|;:,.<>?0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
  
  const scrambleText = (text: string, progress: number) => {
    return text.split('').map((char, index) => {
      // if (char === ' ' || char === '&') return char; // Keep spaces and ampersand
      
      const charProgress = Math.max(0, Math.min(1, (progress - index * 0.05)));
      
      if (charProgress < 0.8) {
        // Scrambled phase
        return scrambleChars[Math.floor(Math.random() * scrambleChars.length)];
      } else if (charProgress < 1) {
        // Transition phase - flickering between scrambled and original
        return Math.random() > 0.5 ? char : scrambleChars[Math.floor(Math.random() * scrambleChars.length)];
      } else {
        // Final phase - original character
        return char;
      }
    }).join('');
  };

  const handleMouseEnter = () => {
    setIsEncrypted(true);
    let progress = 0;
    const interval = setInterval(() => {
      progress += 0.1;
      if (progress <= 1) {
        setDisplayText(scrambleText(originalText, progress));
      } else {
        clearInterval(interval);
        setDisplayText(originalText);
        setTimeout(() => setIsEncrypted(false), 500);
      }
    }, 50);
  };

  const handleMouseLeave = () => {
    if (!isEncrypted) return;
    
    let progress = 1;
    const interval = setInterval(() => {
      progress -= 0.15;
      if (progress > 0) {
        setDisplayText(scrambleText(originalText, progress));
      } else {
        clearInterval(interval);
        setDisplayText(originalText);
        setIsEncrypted(false);
      }
    }, 30);
  };

  return (
    <span 
      className={`${className} transition-all duration-200 cursor-pointer`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {displayText}
    </span>
  );
};

// Animated logo component
const AnimatedLogo = () => {
  const [isActive, setIsActive] = useState(false);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setIsActive(prev => !prev);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative group">
      <img 
        src="/logo.svg" 
        alt="Safermark" 
        className={`h-16 w-16 brightness-0 invert transition-all duration-700 transform ${
          isActive ? 'scale-105 drop-shadow-[0_0_25px_rgba(34,211,238,0.9)]' : 'scale-100 drop-shadow-[0_0_15px_rgba(34,211,238,0.5)]'
        } hover:scale-110 hover:drop-shadow-[0_0_30px_rgba(34,211,238,1)]`} 
      />
      {/* Floating particles around logo */}
      <div className="absolute inset-0 pointer-events-none">
        <div className={`absolute -top-2 -right-2 w-1 h-1 bg-cyan-400 rounded-full transition-all duration-1000 ${isActive ? 'opacity-100 animate-pulse' : 'opacity-30'}`}></div>
        <div className={`absolute -bottom-1 -left-2 w-0.5 h-0.5 bg-blue-400 rounded-full transition-all duration-1000 delay-300 ${isActive ? 'opacity-100 animate-pulse' : 'opacity-40'}`}></div>
        <div className={`absolute top-1 -left-3 w-1.5 h-1.5 bg-cyan-300 rounded-full transition-all duration-1000 delay-500 ${isActive ? 'opacity-80 animate-pulse' : 'opacity-20'}`}></div>
        <div className={`absolute -top-1 left-1/2 w-0.5 h-0.5 bg-blue-300 rounded-full transition-all duration-1000 delay-700 ${isActive ? 'opacity-100 animate-pulse' : 'opacity-25'}`}></div>
      </div>
      {/* Breathing glow effect */}
      <div className={`absolute inset-0 rounded-full bg-gradient-to-r from-cyan-400/10 to-blue-400/10 blur-xl transition-all duration-2000 ${isActive ? 'scale-150 opacity-100' : 'scale-100 opacity-50'}`}></div>
    </div>
  );
};

// Binary rain effect
const BinaryRain = () => {
  const [matrix, setMatrix] = useState<string[][]>([]);

  useEffect(() => {
    const cols = Math.floor(window.innerWidth / 20);
    const rows = Math.floor(window.innerHeight / 20);
    
    const newMatrix = Array.from({ length: rows }, () =>
      Array.from({ length: cols }, () => Math.random() > 0.7 ? '1' : '0')
    );
    
    setMatrix(newMatrix);

    const interval = setInterval(() => {
      setMatrix(prev => 
        prev.map(row => 
          row.map(() => Math.random() > 0.95 ? (Math.random() > 0.5 ? '1' : '0') : '')
        )
      );
    }, 150);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden opacity-5 pointer-events-none font-mono text-xs">
      {matrix.map((row, i) => (
        <div key={i} className="flex">
          {row.map((cell, j) => (
            <span key={j} className="w-5 h-5 text-green-400 animate-pulse">
              {cell}
            </span>
          ))}
        </div>
      ))}
    </div>
  );
};

export default function HomePage() {
  const [visibleElements, setVisibleElements] = useState<Set<number>>(new Set());
  
  // Get configuration from centralized config
  const config = getEnvironmentConfig();
  const freePlan = getFreePlan();
  const monthlyPlan = getRecurringPlan('monthly');
  const yearlyPlan = getRecurringPlan('yearly');
  const paymentMethods = getDisplayPaymentMethods();

  // Handle contact form submission
  const handleContactSubmit = async (formData: {
    name: string;
    email: string;
    subject: string;
    message: string;
  }) => {
    try {
      await apiClient.submitContactForm(formData);
    } catch (error) {
      throw new Error(
        error instanceof Error ? error.message : 'Failed to send message'
      );
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = parseInt(entry.target.getAttribute('data-animate') || '0');
            setVisibleElements(prev => new Set([...prev, id]));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('[data-animate]').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 relative overflow-hidden">
      {/* Background Effects */}
      <BinaryRain />
      <FloatingParticles />
      
      {/* Header */}
      <header className="relative z-10 bg-black/20 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center group">
              <div className="relative">
                <img src="/logo.svg" alt="Safermark" className="h-8 w-8 mr-3 transition-all duration-300 group-hover:scale-110 brightness-0 invert" />
                <div className="absolute inset-0 bg-cyan-400/20 rounded-full blur-lg group-hover:bg-cyan-300/30 transition-all duration-300"></div>
              </div>
              <h1 className="text-2xl font-bold text-white">Safermark</h1>
            </div>
            <div className="flex items-center space-x-4">
              <Link 
                to="/login" 
                className="text-gray-300 hover:text-white font-medium transition-colors duration-200 relative group"
              >
                Sign In
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-cyan-400 transition-all duration-300 group-hover:w-full"></span>
              </Link>
              <Link 
                to="/register" 
                className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:from-cyan-400 hover:to-blue-500 transition-all duration-300 transform hover:scale-105 hover:shadow-lg hover:shadow-cyan-500/25 relative overflow-hidden group"
              >
                <span className="relative z-10">Get Started</span>
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-blue-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <ArrowRight className="inline-block ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative z-10 py-20 px-4 sm:px-6 lg:px-8" data-animate="1">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            {/* Animated title */}
            <div className={`transition-all duration-1000 ${visibleElements.has(1) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <div className="flex justify-center mb-8">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full blur-3xl opacity-40 animate-pulse"></div>
                  <div className="relative bg-black/40 backdrop-blur-sm rounded-full p-8 border border-cyan-400/50 animate-float">
                    <AnimatedLogo />
                  </div>
                </div>
              </div>
              
              <h2 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight animate-fade-in-up">
                <span className="inline-block">Secure</span>{' '}
                <span className="inline-block">Private</span>
                <span className="block bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent animate-gradient-x">
                  <EncryptedText 
                    originalText="Bookmarks & Notes"
                    className="inline-block"
                  />
                </span>
              </h2>
              
              <div className="relative">
                <h3 className="text-xl md:text-2xl font-light text-gray-300 mb-8">
                  with{' '}
                  <span className="relative font-bold text-cyan-400">
                    Zero-Knowledge Encryption
                    <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-cyan-400 to-transparent opacity-50"></div>
                  </span>
                  {' '}· Free Forever for Local Use
                </h3>
              </div>
            </div>

            {/* Animated description */}
            <div className={`transition-all duration-1000 delay-300 ${visibleElements.has(1) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <p className="text-lg text-gray-300 mb-8 max-w-4xl mx-auto leading-relaxed">
                Your bookmarks and notes, encrypted with{' '}
                <span className="text-cyan-400 font-semibold">military-grade AES-256</span>.
                <span className="block mt-2 text-cyan-300 font-medium">
                  Unencrypted vault data never leaves your device - we can't see it, period.
                </span>
              </p>
              
              {/* Key features highlight */}
              <div className="flex flex-wrap justify-center gap-4 mb-12">
                {[
                  { icon: Shield, text: 'Free Forever (Local)', color: 'text-green-400' },
                  { icon: Cloud, text: 'Optional Cloud Sync', color: 'text-blue-400' },
                  { icon: Eye, text: 'Zero-Knowledge', color: 'text-purple-400' },
                  { icon: Key, text: 'Client-Side Encryption', color: 'text-yellow-400' },
                ].map((badge, index) => (
                  <div 
                    key={index} 
                    className="flex items-center space-x-2 bg-black/40 backdrop-blur-sm rounded-full px-4 py-2 border border-white/20 hover:border-cyan-400/50 transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-cyan-500/25 animate-fade-in-up group cursor-default"
                    style={{animationDelay: `${index * 0.1}s`}}
                  >
                    <badge.icon className={`h-4 w-4 ${badge.color} group-hover:animate-pulse`} />
                    <span className="text-gray-200 text-sm font-medium group-hover:text-white transition-colors duration-300">{badge.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA Buttons */}
            <div className={`transition-all duration-1000 delay-500 ${visibleElements.has(1) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href={config.extensionDownloadUrl}
                  className="group relative bg-gradient-to-r from-green-500 to-emerald-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-green-400 hover:to-emerald-500 transition-all duration-300 transform hover:scale-105 hover:-translate-y-1 hover:shadow-2xl hover:shadow-green-500/25 overflow-hidden animate-fade-in-up"
                  style={{animationDelay: '0.6s'}}
                >
                  <span className="relative z-10 flex items-center justify-center">
                    <Chrome className="mr-2 h-5 w-5 group-hover:animate-spin" />
                    Get Free Extension
                    <ArrowRight className="ml-2 h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:animate-pulse" />
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </a>
                <Link 
                  to="/register" 
                  className="group relative bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-cyan-400 hover:to-blue-500 transition-all duration-300 transform hover:scale-105 hover:-translate-y-1 hover:shadow-2xl hover:shadow-cyan-500/25 overflow-hidden animate-fade-in-up"
                  style={{animationDelay: '0.7s'}}
                >
                  <span className="relative z-10 flex items-center justify-center">
                    <Cloud className="mr-2 h-5 w-5 group-hover:animate-bounce" />
                    Get Cloud Sync
                    <ArrowRight className="ml-2 h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:animate-pulse" />
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-blue-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </Link>
                <a 
                  href="#features" 
                  className="group border-2 border-cyan-400/50 text-cyan-400 px-8 py-4 rounded-xl font-bold text-lg hover:bg-cyan-400/10 hover:border-cyan-400 transition-all duration-300 transform hover:scale-105 hover:-translate-y-1 backdrop-blur-sm bg-black/20 animate-fade-in-up"
                  style={{animationDelay: '0.8s'}}
                >
                  <span className="flex items-center justify-center">
                    <Shield className="mr-2 h-5 w-5 group-hover:animate-pulse" />
                    Learn More
                  </span>
                </a>
              </div>
            </div>
          </div>

          {/* Encrypted data visualization */}
          <div className={`transition-all duration-1000 delay-700 ${visibleElements.has(1) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="relative max-w-4xl mx-auto">
              <div className="bg-black/40 backdrop-blur-sm rounded-2xl border border-cyan-400/30 p-8 shadow-2xl hover:border-cyan-400/50 transition-all duration-500 hover:shadow-cyan-500/10">
                <div className="grid lg:grid-cols-3 gap-6">
                  {/* What you see */}
                  <div className="space-y-4">
                    <h4 className="text-blue-400 font-bold text-lg flex items-center group">
                      <Eye className="mr-2 h-5 w-5 group-hover:animate-pulse" />
                      What You See
                    </h4>
                    <div className="bg-slate-800/80 rounded-lg p-4 text-sm text-white border border-blue-500/30 hover:border-blue-400/50 transition-colors duration-300 space-y-2">
                      <div className="flex items-center space-x-2">
                        <Bookmark className="h-4 w-4 text-blue-400" />
                        <span>React Documentation</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <StickyNote className="h-4 w-4 text-green-400" />
                        <span>Meeting Notes - Q4 Plans</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Bookmark className="h-4 w-4 text-purple-400" />
                        <span>Investment Research</span>
                      </div>
                    </div>
                  </div>

                  {/* Encryption process */}
                  <div className="flex flex-col items-center justify-center space-y-4 lg:order-none order-last lg:col-span-1 col-span-full">
                    <div className="text-cyan-400 hover:scale-110 transition-transform duration-300 hidden lg:block">
                      <ArrowRight className="h-6 w-6 animate-pulse hover:animate-bounce" />
                    </div>
                    <div className="text-center">
                      <Shield className="h-8 w-8 text-cyan-400 mx-auto mb-2 animate-pulse" />
                      <p className="text-xs text-cyan-400 font-semibold whitespace-nowrap">AES-256 Encryption</p>
                    </div>
                    <div className="text-cyan-400 hover:scale-110 transition-transform duration-300 hidden lg:block">
                      <ArrowRight className="h-6 w-6 animate-pulse hover:animate-bounce" />
                    </div>
                  </div>

                  {/* What servers see */}
                  <div className="space-y-4">
                    <h4 className="text-red-400 font-bold text-lg flex items-center group">
                      <EyeOff className="mr-2 h-5 w-5 group-hover:animate-pulse" />
                      What Servers See
                    </h4>
                    <div className="bg-black/60 rounded-lg p-4 font-mono text-xs sm:text-sm text-green-400 border border-green-500/30 hover:border-green-400/50 transition-colors duration-300 space-y-2 overflow-hidden">
                      <div className="animate-pulse cursor-default" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        fQIdDblIEA97y5W0dmXdtX4yojqoBwDyfGPlT6k4yAW+rMehE+9vkhMG+70gIsLR
                      </div>
                      <div className="animate-pulse delay-100 cursor-default" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', animationDelay: '0.1s' }}>
                        i7RQh4gyeWcN20ClTA2LLaYEh0yCjY4lTr6LEOoVqKvteXOwzDZTDdYgKswA
                      </div>
                      <div className="animate-pulse delay-200 cursor-default" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', animationDelay: '0.2s' }}>
                        XI0OWq9d4VvBaFXJwBfklPZrj6lTyOhKdoB2ITNtgMHlBpLjLVUrNufBdXiod8s5
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="mt-8 text-center">
                  <div className="inline-flex items-center space-x-4 bg-gradient-to-r from-cyan-900/30 to-blue-900/30 rounded-full px-6 py-3 border border-cyan-500/30">
                    <Lock className="h-5 w-5 text-cyan-400" />
                    <p className="text-gray-300 text-sm">
                      <span className="text-cyan-400 font-semibold">Zero-Knowledge Architecture:</span> Your data is encrypted before leaving your device
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="relative py-20 bg-gradient-to-b from-slate-900 to-black" data-animate="2">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-cyan-900/20 via-transparent to-transparent"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-16 transition-all duration-1000 ${visibleElements.has(2) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <h3 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Your{' '}
              <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                Private Digital Vault
              </span>
            </h3>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Store bookmarks and notes with military-grade encryption. Free forever for local use, premium cloud sync available.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Shield,
                title: 'Free Forever',
                description: 'Chrome extension with unlimited local bookmarks and notes. No subscription required, no time limits.',
                color: 'from-green-400 to-emerald-500',
                delay: 'delay-100'
              },
              {
                icon: Eye,
                title: 'Zero-Knowledge Privacy',
                description: 'Your data is encrypted on your device before it goes anywhere. We literally cannot see your content.',
                color: 'from-purple-400 to-pink-500',
                delay: 'delay-200'
              },
              {
                icon: Cloud,
                title: 'Optional Cloud Sync',
                description: 'Premium users can sync their encrypted vaults across devices with secure cloud storage.',
                color: 'from-blue-400 to-cyan-500',
                delay: 'delay-300'
              }
            ].map((feature, index) => (
              <div 
                key={index}
                className={`group transition-all duration-1000 ${visibleElements.has(2) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'} ${feature.delay}`}
              >
                <div className="relative h-full bg-black/40 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:border-cyan-400/50 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl hover:shadow-cyan-500/10">
                  {/* Animated background */}
                  <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-blue-500/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  
                  {/* Icon with glow effect */}
                  <div className="relative flex justify-center mb-6">
                    <div className="relative">
                      <div className={`absolute inset-0 bg-gradient-to-r ${feature.color} rounded-full blur-xl opacity-30 group-hover:opacity-50 transition-opacity duration-300`}></div>
                      <div className={`relative bg-gradient-to-r ${feature.color} rounded-full p-4`}>
                        <feature.icon className="h-8 w-8 text-white" />
                      </div>
                    </div>
                  </div>
                  
                  <h4 className="text-xl font-bold text-white mb-4 text-center group-hover:text-cyan-400 transition-colors duration-300">
                    {feature.title}
                  </h4>
                  <p className="text-gray-300 text-center leading-relaxed">
                    {feature.description}
                  </p>
                  
                  {/* Hover effect */}
                  <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-b-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
              </div>
            ))}
          </div>

          {/* Additional security features */}
          <div className={`mt-20 transition-all duration-1000 delay-500 ${visibleElements.has(2) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="bg-black/40 backdrop-blur-sm border border-cyan-400/30 rounded-2xl p-8">
              <h4 className="text-2xl font-bold text-white text-center mb-8">
                <span className="text-cyan-400">Military-Grade</span> Security Features
              </h4>
              
              <div className="grid md:grid-cols-4 gap-6">
                {[
                  { icon: Key, text: 'AES-256 Encryption' },
                  { icon: Zap, text: '310K PBKDF2 Iterations' },
                  { icon: Shield, text: 'No Data Logging' },
                  { icon: Lock, text: 'Auto-Lock Timer' }
                ].map((item, index) => (
                  <div key={index} className="flex flex-col items-center text-center group">
                    <div className="relative mb-3">
                      <div className="absolute inset-0 bg-cyan-400/20 rounded-full blur-lg group-hover:bg-cyan-400/30 transition-all duration-300"></div>
                      <item.icon className="relative h-8 w-8 text-cyan-400 group-hover:text-cyan-300 transition-colors duration-300" />
                    </div>
                    <span className="text-gray-300 text-sm font-medium">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="relative py-20 bg-gradient-to-b from-black to-slate-900" data-animate="3">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-blue-900/20 via-transparent to-transparent"></div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-16 transition-all duration-1000 ${visibleElements.has(3) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <h3 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Simple,{' '}
              <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                Transparent Pricing
              </span>
            </h3>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Start free with the Chrome extension, upgrade for cloud features
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Free Plan */}
            <div className={`group transition-all duration-1000 delay-100 ${visibleElements.has(3) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <div className="relative h-full bg-black/40 backdrop-blur-sm border border-white/20 rounded-2xl p-8 hover:border-gray-400/50 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl flex flex-col">
                <div className="text-center flex-1 flex flex-col">
                  <h4 className="text-2xl font-bold text-white mb-2">{freePlan.name}</h4>
                  <div className="text-5xl font-bold text-gray-300 mb-6">
                    {formatPrice(freePlan.price)}<span className="text-lg text-gray-500">/month</span>
                  </div>
                  <ul className="text-left space-y-4 mb-8 flex-1">
                    {freePlan.features.slice(0, 4).map((feature, index) => (
                      <li key={index} className="flex items-center">
                        <CheckCircle className="h-5 w-5 text-green-400 mr-3 flex-shrink-0" />
                        <span className="text-gray-300">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-auto">
                    <a 
                      href={config.extensionDownloadUrl} 
                      className="group relative block w-full bg-gradient-to-r from-gray-600 to-gray-700 text-white py-4 rounded-xl font-bold hover:from-gray-500 hover:to-gray-600 transition-all duration-300 transform hover:scale-105 overflow-hidden"
                    >
                      <span className="relative z-10 flex items-center justify-center">
                        <Chrome className="mr-2 h-5 w-5" />
                        Get Extension
                      </span>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Monthly Plan */}
            <div className={`group transition-all duration-1000 delay-200 ${visibleElements.has(3) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <div className="relative h-full bg-black/40 backdrop-blur-sm border border-blue-400/50 rounded-2xl p-8 hover:border-blue-400 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl hover:shadow-blue-500/25 flex flex-col">
                <div className="relative text-center flex-1 flex flex-col">
                  <h4 className="text-2xl font-bold text-white mb-2">{monthlyPlan?.name}</h4>
                  <div className="text-5xl font-bold text-blue-400 mb-6">
                    {formatPrice(monthlyPlan?.price || 0)}<span className="text-lg text-gray-400">{monthlyPlan?.period}</span>
                  </div>
                  <ul className="text-left space-y-4 mb-8 flex-1">
                    {monthlyPlan?.features.slice(0, 5).map((feature, index) => (
                      <li key={index} className="flex items-center">
                        <CheckCircle className="h-5 w-5 text-blue-400 mr-3 flex-shrink-0" />
                        <span className="text-gray-300">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-auto">
                    <Link 
                      to="/register" 
                      className="group relative block w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-4 rounded-xl font-bold hover:from-blue-400 hover:to-blue-500 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl hover:shadow-blue-500/25 overflow-hidden"
                    >
                      <span className="relative z-10 flex items-center justify-center">
                        Start Monthly
                        <ArrowRight className="ml-2 h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                      </span>
                      <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-blue-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Yearly Plan */}
            <div className={`group transition-all duration-1000 delay-300 ${visibleElements.has(3) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <div className="relative h-full bg-black/40 backdrop-blur-sm border-2 border-cyan-400/50 rounded-2xl p-8 hover:border-cyan-400 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl hover:shadow-cyan-500/25 flex flex-col">
                {/* Popular badge */}
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                  <span className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg whitespace-nowrap">
                    <Sparkles className="inline h-4 w-4 mr-1" />
                    {yearlyPlan?.savings || 'Best Value'}
                  </span>
                </div>
                
                {/* Glow effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                <div className="relative text-center flex-1 flex flex-col">
                  <h4 className="text-2xl font-bold text-white mb-2">{yearlyPlan?.name}</h4>
                  <div className="text-5xl font-bold text-cyan-400 mb-2">
                    {formatPrice(yearlyPlan?.price || 0)}<span className="text-lg text-gray-400">{yearlyPlan?.period}</span>
                  </div>
                  <div className="text-sm text-cyan-300 mb-6 font-medium">
                    ~{formatPrice(Math.round((yearlyPlan?.price || 0) / 12))}/month
                  </div>
                  <ul className="text-left space-y-4 mb-8 flex-1">
                    {yearlyPlan?.features.slice(0, 5).map((feature, index) => (
                      <li key={index} className="flex items-center">
                        <CheckCircle className="h-5 w-5 text-cyan-400 mr-3 flex-shrink-0" />
                        <span className="text-gray-300">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-auto">
                    <Link 
                      to="/register" 
                      className="group relative block w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white py-4 rounded-xl font-bold hover:from-cyan-400 hover:to-blue-500 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl hover:shadow-cyan-500/25 overflow-hidden"
                    >
                      <span className="relative z-10 flex items-center justify-center">
                        <Sparkles className="mr-2 h-5 w-5" />
                        Start Yearly
                        <ArrowRight className="ml-2 h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                      </span>
                      <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-blue-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Payment Methods */}
      <section className="relative py-16 bg-gradient-to-b from-slate-900 to-black" data-animate="4">
        <div className={`max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center transition-all duration-1000 ${visibleElements.has(4) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h3 className="text-3xl font-bold text-white mb-8">
            <span className="text-cyan-400">Flexible</span> Payment Options
          </h3>
          <div className="flex flex-wrap justify-center items-center gap-8">
            {paymentMethods.map((method: PaymentMethod) => (
              <div key={method.id} className={`group flex items-center space-x-3 bg-black/40 backdrop-blur-sm rounded-full px-6 py-3 border border-white/10 hover:border-cyan-400/50 transition-all duration-300 ${method.comingSoon ? 'opacity-60' : ''}`}>
                {method.icon === 'CreditCard' ? (
                  <CreditCard className={`h-6 w-6 ${method.color} group-hover:text-cyan-400 transition-colors duration-300`} />
                ) : (
                  <span className={`${method.color} font-bold text-xl`}>{method.icon}</span>
                )}
                <span className="text-gray-300 font-medium group-hover:text-white transition-colors duration-300">
                  {method.name}
                  {method.comingSoon && <span className="text-xs text-gray-500 ml-1">(Coming Soon)</span>}
                </span>
              </div>
            ))}
          </div>
          <p className="text-gray-400 text-sm mt-6">
            All payments are processed securely through{' '}
            <span className="text-cyan-400 font-semibold">Stripe</span>
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="relative py-20 bg-gradient-to-b from-black to-slate-900" data-animate="5">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-cyan-900/20 via-transparent to-transparent"></div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-12 transition-all duration-1000 ${visibleElements.has(5) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <h3 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Questions?{' '}
              <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                We're Here to Help
              </span>
            </h3>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Get in touch with our team for support, questions, or feedback
            </p>
          </div>
          
          <div className={`transition-all duration-1000 delay-200 ${visibleElements.has(5) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <ContactForm onSubmit={handleContactSubmit} />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative bg-black border-t border-white/10">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/50 to-black"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-4 group">
                <div className="relative">
                  <img src="/logo.svg" alt="Safermark" className="h-6 w-6 mr-2 brightness-0 invert transition-all duration-300 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-cyan-400/20 rounded-full blur-lg group-hover:bg-cyan-300/30 transition-all duration-300"></div>
                </div>
                <span className="font-bold text-white">Safermark</span>
              </div>
              <p className="text-gray-400 leading-relaxed">
                Secure private bookmarks and notes with zero-knowledge encryption. Free forever for local use.
              </p>
            </div>
            
            <div>
              <h5 className="font-bold text-white mb-4">Product</h5>
              <ul className="space-y-3 text-gray-400">
                <li><a href="#features" className="hover:text-cyan-400 transition-colors duration-200 flex items-center group">
                  <ArrowRight className="h-3 w-3 mr-2 opacity-0 group-hover:opacity-100 transition-all duration-200" />
                  Features
                </a></li>
                <li><a href={config.extensionDownloadUrl} className="hover:text-cyan-400 transition-colors duration-200 flex items-center group">
                  <ArrowRight className="h-3 w-3 mr-2 opacity-0 group-hover:opacity-100 transition-all duration-200" />
                  Chrome Extension
                </a></li>
                <li><Link to="/register" className="hover:text-cyan-400 transition-colors duration-200 flex items-center group">
                  <ArrowRight className="h-3 w-3 mr-2 opacity-0 group-hover:opacity-100 transition-all duration-200" />
                  Cloud Sync
                </Link></li>
              </ul>
            </div>
            
            <div>
              <h5 className="font-bold text-white mb-4">Support</h5>
              <ul className="space-y-3 text-gray-400">
                <li><a href="#contact" className="hover:text-cyan-400 transition-colors duration-200 flex items-center group">
                  <ArrowRight className="h-3 w-3 mr-2 opacity-0 group-hover:opacity-100 transition-all duration-200" />
                  Contact Us
                </a></li>
                <li><Link to="/privacy" className="hover:text-cyan-400 transition-colors duration-200 flex items-center group">
                  <ArrowRight className="h-3 w-3 mr-2 opacity-0 group-hover:opacity-100 transition-all duration-200" />
                  Privacy Policy
                </Link></li>
                <li><Link to="/terms" className="hover:text-cyan-400 transition-colors duration-200 flex items-center group">
                  <ArrowRight className="h-3 w-3 mr-2 opacity-0 group-hover:opacity-100 transition-all duration-200" />
                  Terms of Service
                </Link></li>
              </ul>
            </div>
            
            <div>
              <h5 className="font-bold text-white mb-4">Get Started</h5>
              <ul className="space-y-3 text-gray-400">
                <li><a href={config.extensionDownloadUrl} className="hover:text-cyan-400 transition-colors duration-200 flex items-center group">
                  <ArrowRight className="h-3 w-3 mr-2 opacity-0 group-hover:opacity-100 transition-all duration-200" />
                  Download Free
                </a></li>
                <li><Link to="/register" className="hover:text-cyan-400 transition-colors duration-200 flex items-center group">
                  <ArrowRight className="h-3 w-3 mr-2 opacity-0 group-hover:opacity-100 transition-all duration-200" />
                  Create Account
                </Link></li>
                <li><Link to="/login" className="hover:text-cyan-400 transition-colors duration-200 flex items-center group">
                  <ArrowRight className="h-3 w-3 mr-2 opacity-0 group-hover:opacity-100 transition-all duration-200" />
                  Sign In
                </Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-center md:text-left">
              &copy; 2025 Safermark. All rights reserved.
            </p>
            <div className="flex items-center space-x-4 mt-4 md:mt-0">
              <span className="text-gray-400 text-sm">Built with</span>
              <div className="flex items-center space-x-2">
                <Lock className="h-4 w-4 text-cyan-400" />
                <span className="text-cyan-400 text-sm font-medium">Zero-Knowledge Security</span>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}